import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import DatePicker from "../../components/common/DatePicker";
import Select from "../../components/common/Select";
import api from "../../services/api";

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Completed", value: "completed" },
  { label: "Pending", value: "pending" },
  { label: "Failed", value: "failed" },
];

const statusStyle = (s) => {
  const v = (s || "").toLowerCase();
  if (v === "completed") return "bg-emerald-100 text-emerald-700";
  if (v === "pending")   return "bg-amber-100 text-amber-600";
  if (v === "failed")    return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-600";
};

export default function Reports() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateError, setDateError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
  const [exporting, setExporting] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowFormatMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const validateDates = (from, to) => {
    if (from && to && new Date(from) > new Date(to)) {
      setDateError("From date cannot be after To date.");
    } else {
      setDateError("");
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(t);
  }, [searchText, fromDate, toDate, statusFilter]);

  const fetchSheets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", limit);
      params.append("page", page);
      if (searchText) params.append("search", searchText);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      if (statusFilter) params.append("status", statusFilter);

      const res = await api.get(`/sheets?${params.toString()}`);
      const data = res.data?.data || {};
      setSheets(Array.isArray(data.sheets) ? data.sheets : []);
      setMeta(data.meta || { current_page: 1, last_page: 1, per_page: limit, total: 0 });
    } catch {
      toast.error("Failed to fetch reports");
      setSheets([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchText, fromDate, toDate, statusFilter]);

  useEffect(() => { fetchSheets(); }, [fetchSheets]);

  const handleExport = async (format = "csv") => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (searchText) params.append("search", searchText);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      if (statusFilter) params.append("status", statusFilter);
      params.append("format", format);

      const response = await api.get(`/sheets/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const ext = format === "excel" ? "xlsx" : "csv";
      link.setAttribute("download", `reports_${new Date().toISOString().split("T")[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export completed!");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = async (sheet) => {
    try {
      const res = await api.get(`/sheets/${sheet.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", sheet.filename || `report.${sheet.format === "excel" ? "xlsx" : "csv"}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed. Please try again.");
    }
  };

  const [deleteId, setDeleteId] = useState(null);

  const confirmDelete = async () => {
    try {
      await api.delete(`/sheets/${deleteId}`);
      toast.success("Report deleted successfully.");
      setDeleteId(null);
      fetchSheets();
    } catch {
      toast.error("Delete failed. Please try again.");
      setDeleteId(null);
    }
  };

  const clearFilters = () => {
    setSearchText(""); setFromDate(""); setToDate("");
    setStatusFilter(""); setDateError(""); setPage(1);
  };

  const hasFilters = searchText || fromDate || toDate || statusFilter;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-5">
      <div className="max-w-7xl mx-auto space-y-4">

        {/* Delete Confirm Modal */}
        {deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-7 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </div>
              <h3 className="text-[20px] font-bold text-[#191C1D] mb-2">Delete Report?</h3>
              <p className="text-[14px] text-gray-400 mb-7">This action cannot be undone. The report will be permanently deleted.</p>
              <div className="flex items-center gap-3 w-full">
                <button
                  onClick={() => setDeleteId(null)}
                  className="flex-1 py-2.5 rounded-xl text-[14px] font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 border-none cursor-pointer transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-red-500 hover:bg-red-600 border-none cursor-pointer transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="text-[36px] font-bold text-primary-mid">Reports</h1>
          <p className="text-[14px] text-[#6B6471] mt-1">View and download your exported report sheets</p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-[32px] border border-[#CEC3D01A] shadow-[0px_20px_40px_0px_#4B236408] overflow-hidden">
          <div className="text-primary-mid p-[20px] border-b flex justify-between items-center border-[#EDEEEF]">
            <p className="text-[18px] font-bold">Export Sheets</p>
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              className="bg-[#F3F4F5] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none cursor-pointer"
            >
              <option value="10">10 / page</option>
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary-mid border-t-transparent rounded-full animate-spin" />
            </div>
          ) : sheets.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              No reports found.
              {hasFilters && (
                <button onClick={clearFilters} className="block mx-auto mt-2 text-primary-mid text-xs underline">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F8F5FB]">
                    {["#", "Name", "Type", "Format", "Size", "Status", "Completed At", "Action"].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-[12px] font-bold text-[#9B8FA0] uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sheets.map((sheet, i) => (
                    <tr key={sheet.id} className="border-t border-[#F0EBF4] hover:bg-[#FAF8FC] transition-colors">
                      <td className="px-5 py-4 text-[13px] text-[#9B8FA0]">
                        {(page - 1) * limit + i + 1}
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-[13px] font-semibold text-[#191C1D]">{sheet.display_name || "—"}</p>
                        <p className="text-[11px] text-[#9B8FA0] font-mono mt-0.5 truncate max-w-[200px]">{sheet.filename || ""}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-[12px] font-semibold text-primary-mid bg-primary-mid/5 px-2.5 py-1 rounded-full uppercase">
                          {sheet.type || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full uppercase ${sheet.format === "excel" ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"}`}>
                          {sheet.format || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-[13px] text-[#4C444E]">
                        {sheet.file_size_human || "—"}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${statusStyle(sheet.status)}`}>
                          {sheet.status || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-[12px] text-[#9B8FA0] whitespace-nowrap">
                        {sheet.completed_at
                          ? new Date(sheet.completed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                          : "—"}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          {sheet.status === "completed" && (
                            <button
                              onClick={() => handleDownload(sheet)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-primary-mid bg-primary-mid/5 hover:bg-primary-mid/10 transition-colors border-none cursor-pointer"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteId(sheet.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-red-500 bg-red-50 hover:bg-red-100 transition-colors border-none cursor-pointer"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {meta.total > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-6 py-4 border-t border-gray-100 gap-3">
              <span className="text-xs text-[#4C444E]">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, meta.total)} of {meta.total} records
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${page === 1 ? "bg-gray-50 text-gray-300 cursor-not-allowed" : "bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer"}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <span className="text-xs text-[#4C444E]">Page {meta.current_page} of {meta.last_page}</span>
                <button
                  onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                  disabled={page === meta.last_page}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${page === meta.last_page ? "bg-gray-50 text-gray-300 cursor-not-allowed" : "bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer"}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
